import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

interface CollapsibleSummaryHeaderProps {
    leading: ReactNode;
    trailing?: ReactNode;
}

/** Vertical divider height matches previous `h-4` divider. */
const DIVIDER_HEIGHT_PX = 16;
/** Matches parent `gap-x-2` (0.5rem). */
const ROW_GAP_PX = 8;

export function CollapsibleSummaryHeader({ leading, trailing }: CollapsibleSummaryHeaderProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const leadingRef = useRef<HTMLDivElement>(null);
    const trailingRef = useRef<HTMLDivElement>(null);
    const hasTrailing = trailing != null;
    const [dividerStyle, setDividerStyle] = useState<{ left: number; top: number } | null>(null);

    useLayoutEffect(() => {
        if (!hasTrailing) {
            setDividerStyle(null);
            return;
        }

        const container = containerRef.current;
        const leadingEl = leadingRef.current;
        const trailingEl = trailingRef.current;
        if (!container || !leadingEl || !trailingEl) return;

        const update = () => {
            const containerRect = container.getBoundingClientRect();
            const leadingRect = leadingEl.getBoundingClientRect();
            const trailingRect = trailingEl.getBoundingClientRect();

            const isWrapped = trailingRect.top > leadingRect.top + 2;
            if (isWrapped) {
                setDividerStyle(null);
                return;
            }

            // Paint the divider in the gap between leading and trailing without
            // participating in flex layout — otherwise toggling it changes width
            // and causes a wrap/unwrap feedback loop on borderline titles.
            const left = leadingRect.right - containerRect.left + ROW_GAP_PX / 2;
            const top = leadingRect.top - containerRect.top + (leadingRect.height - DIVIDER_HEIGHT_PX) / 2;
            setDividerStyle({ left, top });
        };

        update();

        const observer = new ResizeObserver(update);
        observer.observe(container);
        observer.observe(leadingEl);
        observer.observe(trailingEl);

        return () => observer.disconnect();
    }, [hasTrailing, leading, trailing]);

    return (
        <div
            ref={containerRef}
            className="relative flex flex-wrap items-center gap-x-2 gap-y-1 text-sm min-w-0 flex-1"
        >
            <div ref={leadingRef} className="flex items-center gap-x-2 gap-y-1 shrink-0">
                {leading}
            </div>
            {hasTrailing && (
                <div ref={trailingRef} className="flex items-center flex-wrap gap-x-2 gap-y-1 min-w-0">
                    {trailing}
                </div>
            )}
            {dividerStyle && (
                <div
                    className="absolute w-px bg-gray-300 pointer-events-none"
                    style={{
                        left: dividerStyle.left,
                        top: dividerStyle.top,
                        height: DIVIDER_HEIGHT_PX,
                    }}
                    aria-hidden
                />
            )}
        </div>
    );
}
